import { AsyncPipe } from '@angular/common';
import { Component, inject, OnInit, ViewChild } from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { Router, RouterLink } from '@angular/router';
import {
  EmailValidationRules,
  FeatureFlags,
  StorageKeys,
} from '@core/constants';
import { JWT_INSTANCE } from '@core/helpers/jwt';
import { DecodedToken } from '@core/models/decoded-token';
import { UserModel } from '@core/models/user-model';
import { AuthenticationService } from '@core/services/authentication/authentication.service';
import { LoginService } from '@core/services/login/login.service';
import { Login } from '@core/state/auth/auth.actions';
import { AuthState } from '@core/state/auth/auth.reducer';
import { Store } from '@ngrx/store';
import { FormValidationMessageModule } from '@shipmercury/mercury-ui';
import { RecaptchaComponent, RecaptchaModule } from 'ng-recaptcha-2';
import { ButtonDirective, ButtonLabel } from 'primeng/button';
import { CheckboxModule } from 'primeng/checkbox';
import { InputTextModule } from 'primeng/inputtext';
import { PasswordModule } from 'primeng/password';
import { BehaviorSubject } from 'rxjs';
import { PublicBaseComponent } from 'src/app/public-base.component';

@Component({
  selector: 'au-unified-login',
  templateUrl: './unified-login.component.html',
  styleUrls: ['./unified-login.component.scss'],
  imports: [
    ReactiveFormsModule,
    InputTextModule,
    FormValidationMessageModule,
    PasswordModule,
    CheckboxModule,
    RouterLink,
    ButtonDirective,
    AsyncPipe,
    RecaptchaModule,
    ButtonLabel,
  ],
})
export class UnifiedLoginComponent
  extends PublicBaseComponent
  implements OnInit
{
  @ViewChild('captchaRef') captchaRef!: RecaptchaComponent;
  loginForm!: FormGroup;
  mercuryLogoPath = 'assets/images/logo-original.svg';
  isCommonPage: boolean = false;
  jwt = inject(JWT_INSTANCE);
  formSubmitted: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  enableRecaptcha: boolean = true;

  constructor(
    private authService: AuthenticationService,
    private loginService: LoginService,
    private router: Router,
    private fb: FormBuilder,
    private store: Store<AuthState>
  ) {
    super();

    if (this.featureFlagService.unleashClient?.toggles.length > 0) {
      this.initFeatureFlags();
    }
    if (this.disableByAutomationUserAgent()) {
      this.enableRecaptcha = false;
    }
  }

  initFeatureFlags() {
    this.enableRecaptcha = this.featureFlagService.featureFlagEnabled(
      FeatureFlags.ENABLE_RECAPTCHA_LOGIN
    );
  }

  ngOnInit(): void {
    this.storageService.delete(StorageKeys.authUser);
    this.storageService.delete(StorageKeys.captchaResponse);

    this.loginForm = this.fb.group({
      email: ['', EmailValidationRules],
      password: ['', Validators.required],
    });

    this.isCommonPage = this.router.url === '/commonlogin';
  }

  submitRequestWithCaptcha(): void {
    this.captchaRef.execute();
  }

  resolved(captchaResponse: any) {
    if (captchaResponse) {
      this.storageService.save(captchaResponse, StorageKeys.captchaResponse);
      this.login();
      this.captchaRef.reset();
    }
  }

  login(): void {
    if (!this.loginForm.valid) {
      Object.keys(this.loginForm.controls).forEach((key) => {
        this.loginForm.get(key)?.markAsDirty();
      });

      return;
    }
    this.formSubmitted.next(true);
    const { email, password } = this.loginForm.value;
    this.loginService.login(email, password).subscribe((res) => {
      if (res && res.success) {
        this.saveToken(res.data);
        this.authService.redirectUrl
          ? this.router.navigateByUrl(this.authService.redirectUrl)
          : this.router.navigateByUrl('/shipping/ship');
      } else {
        this.formSubmitted.next(false);
      }
    });
  }

  navigateToRequestAccount() {
    this.router.navigateByUrl('/request-account');
  }

  navigateToBookShipment() {
    this.router.navigateByUrl('ship-lite');
  }

  private saveToken(data: any) {
    let decodedToken = this.jwt.decodeToken<DecodedToken>(data.accessToken);
    if (!decodedToken) return;

    let user: UserModel = {
      name: decodedToken.name,
      email: decodedToken.email,
      customerId: decodedToken.customerId,
      authToken: data.accessToken,
      refreshToken: data.refreshToken,
      userId: decodedToken.userId,
    };
    this.store.dispatch(Login({ user }));

    return user;
  }
}
